import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { FaPlus } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { trackProductListSwatchClicked } from '~/shared/actions';
import featureNames from '~/shared/constants/featureFlags';
import { ProductTag } from '~/shared/constants/products';
import { navigateToPathnameFromHref } from '~/shared/utils/navigateToPathnameFromHref';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  GridColorSwatches,
  ProductSelectorType,
  mobile,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { useIntl } from '~/techstyle-shared/react-intl';
import {
  getProductAvailabilityForUser,
  ProductUserStatus,
} from '~/techstyle-shared/react-products';

import useMaxSwatchesToRender from '../../utils/useMaxSwatchesToRender';

const inputStyle = css`
  border-radius: 28px;
  padding-right: 8px;
  padding-left: 0;
  border: 2px solid ${({ theme }) => theme.colors.swatchInputBorder};
  input:checked + && {
    background-color: transparent;
    border: 2px solid ${({ theme }) => theme.colors.focusText};
    box-shadow: inset 0px 0px 0px 3px
      ${({ theme }) => theme.colors.palette.white};
  }
  input:checked:focus + && {
    box-shadow:
      inset 0px 0px 0px 4px ${({ theme }) => theme.colors.palette.white},
      0 0 0 1px ${({ theme }) => theme.colors.gridSwatchInputCheckedFocus};
  }
  ${mobile`
      padding-right: 6px;
  `}
  ${({ theme }) => theme.gridCellStyles.gridSwatchInputStyle}
`;

const ColorSelector = styled(GridColorSwatches.Selector)`
  padding-left: 0;
`;

const GridColorSwatchWrapper = styled.div`
  ${({ theme }) => theme.gridCellStyles.gridColorSwatchWrapper}
`;

const PlusIcon = styled(FaPlus)`
  display: flex;
  align-items: center;
  padding-left: 8px;
`;

const GridSwatchesMoreColorsLink = styled.div`
  color: ${({ theme }) => theme.colors.swatchesMoreColorsLink};
  font-weight: 700;
  line-height: ${16 / 11};
  font-size: 11px;
  text-transform: uppercase;

  a:link &,
  &:link {
    text-decoration: none;
  }
`;

const GridSwatches = ({ href, swatchData }) => {
  const { isMobile } = useBreakpoint();
  const currentUserStatus = useMembership();
  const { activeProduct, handleColorChange } = GridColorSwatches.useContext();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { enabled: removeOriginFlag } = useHasFeature(
    featureNames.REMOVE_ORIGIN
  );
  const [originalColor] = useState(
    GridColorSwatches.getSwatchItem(activeProduct).color
  );

  // 4 is for mobile, tablet
  // 5 is for desktop
  const maxSwatchesToRender = useMaxSwatchesToRender();
  const filteredSwatchData = swatchData.filter((item) => {
    let requiredUserStatus = [];

    if (item?.tagIdList?.includes(ProductTag.leadOnly)) {
      requiredUserStatus = [ProductUserStatus.LEAD_ONLY];
    } else if (item?.tagIdList?.includes(ProductTag.vipOnly)) {
      requiredUserStatus = [ProductUserStatus.VIP_ONLY];
    }

    if (
      removeOriginFlag &&
      item?.tagIdList?.includes(ProductTag.removeOrigin)
    ) {
      return false;
    }

    return getProductAvailabilityForUser({
      currentUserStatus,
      requiredUserStatus,
    });
  });

  const showPlusSign = filteredSwatchData.length > maxSwatchesToRender;
  const actualSwatchData = filteredSwatchData.splice(0, maxSwatchesToRender);

  const handleMouseEnter = useCallback(
    (option) => {
      handleColorChange(option);
    },
    [handleColorChange]
  );

  const handleClick = useCallback(
    (event, swatch) => {
      dispatch(
        trackProductListSwatchClicked({
          ...swatch,
          originalColor,
        })
      );
      if (isMobile) {
        handleColorChange(swatch);
      } else {
        setTimeout(() => {
          navigateToPathnameFromHref(href);
        }, 300);
      }
    },
    [dispatch, originalColor, isMobile, handleColorChange, href]
  );

  const moreColorsAriaLabel = intl.formatMessage({
    id: 'grid.more_colors_aria_label',
    defaultMessage: 'more colors link',
  });

  return (
    <GridColorSwatchWrapper>
      <GridColorSwatches.SelectorList data-testid="grid-color-swatches">
        {actualSwatchData.map((swatch, index) => {
          const isChecked =
            activeProduct.masterProductId === swatch.masterProductId;

          return (
            <ColorSelector
              key={swatch.masterProductId}
              hideLabel
              label={swatch.color}
              selectorType={ProductSelectorType.SWATCH}
              swatchImageUrl={swatch.swatchUrl}
              option={swatch}
              onMouseEnter={handleMouseEnter}
              onClick={(e) => handleClick(e, swatch)}
              inputStyle={inputStyle}
              isChecked={isChecked}
              data-testid="grid-color-swatch"
            />
          );
        })}
      </GridColorSwatches.SelectorList>
      {showPlusSign ? (
        <GridSwatchesMoreColorsLink
          as="a"
          href={href}
          tabIndex={-1}
          aria-label={moreColorsAriaLabel}
          data-testid="grid-swatches-more-colors"
        >
          <PlusIcon size={26} />
        </GridSwatchesMoreColorsLink>
      ) : null}
    </GridColorSwatchWrapper>
  );
};

GridSwatches.propTypes = {
  href: PropTypes.string,
  swatchData: PropTypes.array,
};

export default GridSwatches;
